import React, { useCallback, useEffect } from 'react';
import {
  Extrapolation,
  interpolate,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { Keyboard, LayoutChangeEvent, StyleSheet, View } from 'react-native';
import {
  NavigationHeaderContent,
  Props as NavigationHeaderContentProps,
} from './navigation-header-content';
import { useIndexScrollbarActiveContext } from '@/contexts/index-scrollbar-context';
import { useLimitedPerformanceEnabled } from '@/hooks/use-limited-performance';
import { useNavigation } from '@react-navigation/native';

type LayoutChangeCallbackType = (e: LayoutChangeEvent) => void;

export type Props = {
  animate?: boolean;
  onLayout?: LayoutChangeCallbackType;
  scrollY?: SharedValue<number>;
} & NavigationHeaderContentProps;

export const NavigationHeader: React.FC<Props> = ({
  animate = false,
  contentContainerStyle,
  scrollY,
  onLayout,
  ...rest
}) => {
  const limitedPerformanceEnabled = useLimitedPerformanceEnabled();
  const indexScrollbarActive = useIndexScrollbarActiveContext();
  const { goBack } = useNavigation();

  const contentHeightValue = useSharedValue(0);
  const clampedScroll = useSharedValue(0);
  const indexScrollbarActiveValue = useSharedValue(indexScrollbarActive);
  const limitedPerformanceValue = useSharedValue(limitedPerformanceEnabled);

  useEffect(() => {
    indexScrollbarActiveValue.value = indexScrollbarActive;
    if (indexScrollbarActive) {
      clampedScroll.value = 0;
    }
  }, [indexScrollbarActive, indexScrollbarActiveValue, clampedScroll]);

  useEffect(() => {
    limitedPerformanceValue.value = limitedPerformanceEnabled;
  }, [limitedPerformanceEnabled, limitedPerformanceValue]);

  // Accumulate scroll deltas into a clamped value so the header hides when
  // scrolling down and reappears as soon as the user scrolls back up
  useAnimatedReaction(
    () => scrollY?.value ?? 0,
    (current, previous) => {
      if (previous === null || !scrollY) {
        return;
      }

      const delta = current - previous;
      clampedScroll.value = Math.max(
        0,
        Math.min(clampedScroll.value + delta, contentHeightValue.value),
      );
    },
    [scrollY],
  );

  const animatedAfterContainerStyle = useAnimatedStyle(() => {
    // Disable styles so the navigation header completely renders while the
    // user is interacting with the index scrollbar
    if (
      !scrollY ||
      limitedPerformanceValue.value ||
      contentHeightValue.value === 0 ||
      indexScrollbarActiveValue.value
    ) {
      return { maxHeight: 10000, opacity: 1 };
    }

    const opacity = interpolate(
      clampedScroll.value,
      [0, contentHeightValue.value],
      [1, 0],
      Extrapolation.CLAMP,
    );
    return {
      // maxHeight is used so the container no longer takes up any height
      // so touch events can propagate to the views below it when it's 0
      maxHeight: opacity < 0.001 ? 0 : 10000, // 0 when fully transparent, arbitrarily large otherwise
      opacity,
    };
  });

  const animatedContentContainerStyle = useAnimatedStyle(() => {
    if (
      !scrollY ||
      limitedPerformanceValue.value ||
      contentHeightValue.value === 0 ||
      indexScrollbarActiveValue.value
    ) {
      return {};
    }

    return {
      height: interpolate(
        clampedScroll.value,
        [0, contentHeightValue.value],
        [contentHeightValue.value, 0],
        Extrapolation.CLAMP,
      ),
      opacity: interpolate(
        clampedScroll.value,
        [0, contentHeightValue.value],
        [1, 0],
        Extrapolation.CLAMP,
      ),
    };
  });

  const onContentLayoutCallback = useCallback(
    (e: LayoutChangeEvent) => {
      contentHeightValue.value = e.nativeEvent.layout.height;
    },
    [contentHeightValue],
  );

  const handleBackPress = useCallback(() => {
    /**
     * Dismiss the keyboard before navigating back.
     * Android's keyboard dismissal is asynchronous and may not complete before the screen transition.
     */
    Keyboard.dismiss();
    goBack();
  }, [goBack]);

  return animate ? (
    // Since the animated navigation header's height can change based on the page
    // scroll position, render a shadow header off-screen that's responsible for
    // measuring the header's true overall height
    <>
      <View style={styles.animatedContainer}>
        <NavigationHeaderContent
          afterContainerStyle={animatedAfterContainerStyle}
          contentContainerStyle={[
            contentContainerStyle,
            animatedContentContainerStyle,
          ]}
          onBackPress={handleBackPress}
          {...rest}
        />
      </View>
      <View
        onLayout={onLayout}
        pointerEvents="none"
        style={styles.shadowContainer}
      >
        <NavigationHeaderContent
          onContentLayout={onContentLayoutCallback}
          {...rest}
        />
      </View>
    </>
  ) : (
    <NavigationHeaderContent
      contentContainerStyle={contentContainerStyle}
      onBackPress={handleBackPress}
      {...rest}
    />
  );
};

const styles = StyleSheet.create({
  animatedContainer: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  shadowContainer: {
    // Ensures rendering off-screen
    bottom: '100%',
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
  },
});
